<script>
  import { onMount, onDestroy } from 'svelte';
  import ContainerNode from './ContainerNode.svelte';
  import MoveItemModal from './MoveItemModal/index.svelte';

  let { containers = [], canManageContainers, propertyId, paginationMeta = {}, onCountUpdate } = $props();

  // The container currently being moved, mapped into the shape MoveItemModal
  // expects. Containers are items, so the move endpoint/modal are shared; we
  // just translate the container vocabulary (label, parent_container_id) and
  // pass the counts that drive the "also moves N items and M containers" note.
  let containerToMove = $state(null);

  function handleMove(container) {
    containerToMove = {
      id: container.id,
      name: container.label,
      property_id: container.property_id,
      space_id: container.space_id,
      container_id: container.parent_container_id || '',
      container_type: container.container_type,
      item_count: container.item_count,
      child_container_count: container.child_container_count
    };
  }

  // Track expanded state for all containers
  let expandedIds = $state([]);
  // Pagination state
  let allContainers = $state([...containers]);
  let isLoading = $state(false);
  let currentPage = $state(1);
  let hasMorePages = $state(paginationMeta.has_more_pages || false);
  let totalCount = $state(paginationMeta.total_count || containers.length);
  let scrollContainer = $state();

  function toggleContainer(containerId) {
    // Toggle the container's expanded state
    if (expandedIds.includes(containerId)) {
      // When collapsing, also collapse all child containers
      const childIds = allContainers
        .filter(c => c.parent_container_id === containerId)
        .map(c => c.id);
      expandedIds = expandedIds.filter(id => id !== containerId && !childIds.includes(id));
    } else {
      // When expanding, just add this container
      expandedIds = [...expandedIds, containerId];
    }
  }



  function isExpanded(containerId) {
    return expandedIds.includes(containerId);
  }

  // Load more containers
  async function loadMoreContainers() {
    if (isLoading || !hasMorePages) return;
    
    isLoading = true;
    
    try {
      const response = await fetch(
        `/properties/${propertyId}/containers?page=${currentPage + 1}`,
        {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to load containers');
      }
      
      const data = await response.json();
      
      allContainers = [...allContainers, ...data.containers];
      currentPage = data.pagy.page;
      hasMorePages = data.pagy.page < data.pagy.pages;
      
      // Update total count if provided
      if (data.pagy.total_count !== undefined) {
        totalCount = data.pagy.total_count;
      }
      
      // Notify parent of count update
      if (onCountUpdate) {
        onCountUpdate(allContainers.length, totalCount);
      }
      
    } catch (error) {
      console.error('Error loading containers:', error);
    } finally {
      isLoading = false;
    }
  }

  // Infinite scroll handler
  function handleScroll() {
    if (!scrollContainer) return;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const threshold = 50; // Load more when 50px from bottom
    
    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      loadMoreContainers();
    }
  }

  // Get root containers. A container whose parent hasn't been loaded yet
  // (flat pagination by updated_at can deliver children before parents)
  // renders at the top level until its parent arrives, then re-nests.
  const loadedIds = $derived(new Set(allContainers.map(c => c.id)));
  const rootContainers = $derived(allContainers.filter(c =>
    !c.parent_container_id || !loadedIds.has(c.parent_container_id)
  ));

  // Check if we should load more on mount
  onMount(() => {
    // Notify parent of initial count
    if (onCountUpdate) {
      onCountUpdate(allContainers.length, totalCount);
    }
    
    // If we have less than 10 containers total, there might be more to load
    if (allContainers.length < 10 && hasMorePages) {
      loadMoreContainers();
    }
  });

</script>

<div class="space-y-4">
  {#if allContainers.length === 0 && !isLoading}
    <div class="text-center py-8">
      <div class="text-gray-400 mb-4">
        <svg class="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      </div>
      <p class="text-gray-500">No containers added yet.</p>
    </div>
  {:else}
    
    <!-- Scrollable container for infinite scroll -->
    <div
      bind:this={scrollContainer}
      onscroll={handleScroll}
      class="max-h-96 overflow-y-auto space-y-2.5 pr-1"
    >
      {#each rootContainers as container}
        <ContainerNode
          {container}
          containers={allContainers}
          {canManageContainers}
          {isExpanded}
          {toggleContainer}
          onMove={canManageContainers ? handleMove : null}
          level={0}
        />
      {/each}

      <!-- Loading indicator -->
      {#if isLoading}
        <div class="flex items-center justify-center gap-2 py-3 text-xs text-gray-400">
          <span class="h-3.5 w-3.5 rounded-full border-2 border-gray-200 border-t-brand-600 animate-spin"></span>
          Loading more containers…
        </div>
      {/if}

      <!-- End of results message -->
      {#if !hasMorePages && allContainers.length > 0 && !isLoading}
        <div class="text-center py-3 text-xs text-gray-400">
          All {allContainers.length} containers loaded
        </div>
      {/if}
    </div>

    {#if hasMorePages && !isLoading}
      <div class="text-center pt-2 text-xs text-gray-400">
        Showing {allContainers.length} of {totalCount} · more load as you scroll
      </div>
    {/if}
  {/if}
</div>

<MoveItemModal
  open={!!containerToMove}
  item={containerToMove}
  onClose={() => (containerToMove = null)}
/>
